'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';

export type InternalLinksVariant =
  | 'homepage' | 'service' | 'brand' | 'city' | 'contact' | 'faq' | 'full';

interface SiteInternalLinksProps {
  variant?: InternalLinksVariant;
  currentSlug?: string;
  currentCity?: string;
  currentService?: string;
  currentBrand?: string;
}

const coreServices = [
  { href: '/roetfilter-reinigen', label: 'Roetfilter Reinigen', desc: 'DPF reiniging vanaf €195', icon: '🔧' },
  { href: '/roetfilter-revisie', label: 'Roetfilter Revisie', desc: 'Volledige DPF herstelling', icon: '⚙️' },
  { href: '/roetfilter-vervangen', label: 'Roetfilter Vervangen', desc: 'Nieuwe DPF montage', icon: '🔄' },
  { href: '/roetfilter-verwijderen', label: 'Roetfilter Verwijderen', desc: 'Legale verwijdering', icon: '🚫' },
  { href: '/katalysator-problemen', label: 'Katalysator Problemen', desc: 'Reiniging & vervanging', icon: '🏭' },
  { href: '/egr-klep-problemen', label: 'EGR-klep Problemen', desc: 'Diagnose & herstelling', icon: '🌀' },
  { href: '/adblue-problemen', label: 'AdBlue Problemen', desc: 'SCR-systeem specialist', icon: '💧' },
  { href: '/roetfilter-reinigen-bestelwagen', label: 'Bestelwagen DPF', desc: 'Bestelwagen specialist', icon: '🚐' },
  { href: '/afgekeurd-voor-roet', label: 'Afgekeurd voor Roet?', desc: 'Wat nu na afkeuring?', icon: '🚨' },
];

const brandPages = [
  { href: '/audi-roetfilter', label: 'Audi', initial: 'A' },
  { href: '/bmw-roetfilter', label: 'BMW', initial: 'B' },
  { href: '/citroen-roetfilter', label: 'Citroën', initial: 'C' },
  { href: '/honda-roetfilter', label: 'Honda', initial: 'H' },
  { href: '/hyundai-roetfilter', label: 'Hyundai', initial: 'H' },
  { href: '/jaguar-roetfilter', label: 'Jaguar', initial: 'J' },
  { href: '/land-rover-roetfilter', label: 'Land Rover', initial: 'L' },
  { href: '/mazda-roetfilter', label: 'Mazda', initial: 'M' },
  { href: '/mercedes-roetfilter', label: 'Mercedes', initial: 'M' },
  { href: '/peugeot-roetfilter', label: 'Peugeot', initial: 'P' },
  { href: '/renault-roetfilter', label: 'Renault', initial: 'R' },
  { href: '/seat-roetfilter', label: 'Seat', initial: 'S' },
  { href: '/skoda-roetfilter', label: 'Skoda', initial: 'S' },
  { href: '/subaru-roetfilter', label: 'Subaru', initial: 'S' },
  { href: '/volkswagen-roetfilter', label: 'Volkswagen', initial: 'V' },
  { href: '/volvo-roetfilter', label: 'Volvo', initial: 'V' },
];

const priorityCities = [
  { href: '/roetfilter-reinigen-lokeren', label: 'Lokeren' },
  { href: '/roetfilter-reinigen-gent', label: 'Gent' },
  { href: '/roetfilter-reinigen-sint-niklaas', label: 'Sint-Niklaas' },
  { href: '/roetfilter-reinigen-dendermonde', label: 'Dendermonde' },
  { href: '/roetfilter-reinigen-aalst', label: 'Aalst' },
  { href: '/locaties/antwerpen', label: 'Antwerpen' },
  { href: '/locaties/mechelen', label: 'Mechelen' },
  { href: '/locaties/brussel', label: 'Brussel' },
  { href: '/locaties/hamme', label: 'Hamme' },
  { href: '/roetfilter-reinigen-temse', label: 'Temse' },
  { href: '/locaties/zele', label: 'Zele' },
  { href: '/locaties/waasmunster', label: 'Waasmunster' },
];

const oostVlaanderenCities = [
  { href: '/roetfilter-reinigen-aalst', label: 'Aalst' },
  { href: '/locaties/aalter', label: 'Aalter' },
  { href: '/locaties/assenede', label: 'Assenede' },
  { href: '/locaties/berlare', label: 'Berlare' },
  { href: '/locaties/beveren-kruibeke-zwijndrecht', label: 'Beveren' },
  { href: '/locaties/buggenhout', label: 'Buggenhout' },
  { href: '/roetfilter-reinigen-deinze', label: 'Deinze' },
  { href: '/locaties/denderleeuw', label: 'Denderleeuw' },
  { href: '/roetfilter-reinigen-dendermonde', label: 'Dendermonde' },
  { href: '/locaties/destelbergen', label: 'Destelbergen' },
  { href: '/locaties/eeklo', label: 'Eeklo' },
  { href: '/roetfilter-reinigen-evergem', label: 'Evergem' },
  { href: '/roetfilter-reinigen-gent', label: 'Gent' },
  { href: '/locaties/hamme', label: 'Hamme' },
  { href: '/locaties/laarne', label: 'Laarne' },
  { href: '/locaties/lebbeke', label: 'Lebbeke' },
  { href: '/locaties/lede', label: 'Lede' },
  { href: '/locaties/lochristi', label: 'Lochristi' },
  { href: '/roetfilter-reinigen-lokeren', label: 'Lokeren' },
  { href: '/locaties/merelbeke-melle', label: 'Merelbeke' },
  { href: '/locaties/ninove', label: 'Ninove' },
  { href: '/locaties/sint-gillis-waas', label: 'Sint-Gillis-Waas' },
  { href: '/roetfilter-reinigen-sint-niklaas', label: 'Sint-Niklaas' },
  { href: '/locaties/stekene', label: 'Stekene' },
  { href: '/roetfilter-reinigen-temse', label: 'Temse' },
  { href: '/locaties/waasmunster', label: 'Waasmunster' },
  { href: '/locaties/wetteren', label: 'Wetteren' },
  { href: '/locaties/zele', label: 'Zele' },
  { href: '/locaties/zelzate', label: 'Zelzate' },
  { href: '/locaties/zottegem', label: 'Zottegem' },
];

const antwerpenCities = [
  { href: '/locaties/antwerpen', label: 'Antwerpen' },
  { href: '/locaties/boom', label: 'Boom' },
  { href: '/locaties/bornem', label: 'Bornem' },
  { href: '/locaties/duffel', label: 'Duffel' },
  { href: '/locaties/edegem', label: 'Edegem' },
  { href: '/locaties/geel', label: 'Geel' },
  { href: '/locaties/heist-op-den-berg', label: 'Heist-op-den-Berg' },
  { href: '/locaties/herentals', label: 'Herentals' },
  { href: '/locaties/kontich', label: 'Kontich' },
  { href: '/locaties/lier', label: 'Lier' },
  { href: '/locaties/mechelen', label: 'Mechelen' },
  { href: '/locaties/mol', label: 'Mol' },
  { href: '/locaties/mortsel', label: 'Mortsel' },
  { href: '/locaties/puurs-sint-amands', label: 'Puurs-Sint-Amands' },
  { href: '/locaties/rumst', label: 'Rumst' },
  { href: '/locaties/turnhout', label: 'Turnhout' },
  { href: '/locaties/willebroek', label: 'Willebroek' },
];

const brusselsCities = [
  { href: '/locaties/anderlecht', label: 'Anderlecht' },
  { href: '/locaties/brussel', label: 'Brussel' },
  { href: '/locaties/etterbeek', label: 'Etterbeek' },
  { href: '/locaties/evere', label: 'Evere' },
  { href: '/locaties/ixelles', label: 'Ixelles' },
  { href: '/locaties/jette', label: 'Jette' },
  { href: '/locaties/molenbeek-saint-jean', label: 'Molenbeek' },
  { href: '/locaties/schaarbeek', label: 'Schaarbeek' },
  { href: '/locaties/ukkel', label: 'Ukkel' },
  { href: '/locaties/woluwe-saint-lambert', label: 'Woluwe-Saint-Lambert' },
];

const dedicatedCityPages = [
  { href: '/roetfilter-reinigen-gent', label: 'Gent' },
  { href: '/roetfilter-reinigen-sint-niklaas', label: 'Sint-Niklaas' },
  { href: '/roetfilter-reinigen-dendermonde', label: 'Dendermonde' },
  { href: '/roetfilter-reinigen-lokeren', label: 'Lokeren' },
  { href: '/roetfilter-reinigen-aalst', label: 'Aalst' },
  { href: '/roetfilter-reinigen-temse', label: 'Temse' },
  { href: '/roetfilter-reinigen-deinze', label: 'Deinze' },
  { href: '/roetfilter-reinigen-evergem', label: 'Evergem' },
];

// Smooth animated accordion using CSS max-height transition
interface AccordionProps {
  title: string;
  badge?: string;
  badgeColor?: string;
  icon?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Accordion({ title, badge, badgeColor = 'bg-primary/10 text-primary', icon, children, defaultOpen = false }: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>(defaultOpen ? 'auto' : '0px');

  useEffect(() => {
    if (open) {
      const el = contentRef.current;
      if (el) {
        setHeight(`${el.scrollHeight}px`);
        // After transition, set to auto to allow dynamic content
        const timer = setTimeout(() => setHeight('auto'), 300);
        return () => clearTimeout(timer);
      }
    } else {
      // First set to current height, then animate to 0
      const el = contentRef.current;
      if (el) {
        setHeight(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setHeight('0px'));
        });
      }
    }
  }, [open]);

  return (
    <div className={`rounded-xl overflow-hidden border transition-all duration-200 ${open ? 'border-primary/30 shadow-sm' : 'border-border'} bg-white`}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-5 py-4 transition-colors text-left ${open ? 'bg-primary/5' : 'bg-white hover:bg-secondary/20'}`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-3">
          {icon && (
            <span className={`flex items-center justify-center w-8 h-8 rounded-lg text-base ${open ? 'bg-primary text-white' : 'bg-secondary/50 text-foreground'} transition-colors duration-200`}>
              {icon}
            </span>
          )}
          <span className="text-sm font-bold text-foreground">{title}</span>
          {badge && (
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${badgeColor}`}>
              {badge}
            </span>
          )}
        </span>
        <span className={`flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200 ${open ? 'bg-primary text-white rotate-180' : 'bg-secondary/50 text-muted-foreground'}`}>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      <div
        ref={contentRef}
        style={{ height, overflow: 'hidden', transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        <div className="px-5 py-5 border-t border-border/50 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function SiteInternalLinks({
  variant = 'full',
  currentSlug,
  currentService,
  currentBrand,
}: SiteInternalLinksProps) {
  const showServices = ['homepage', 'city', 'brand', 'contact', 'faq', 'full'].includes(variant);
  const showBrands = ['homepage', 'service', 'city', 'full'].includes(variant);
  const showCities = ['homepage', 'service', 'brand', 'contact', 'faq', 'full'].includes(variant);
  const showAllRegions = ['homepage', 'full'].includes(variant);

  return (
    <section className="py-16 bg-gradient-to-b from-secondary/20 to-secondary/5 border-t border-border">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-4 py-1.5 rounded-full mb-3">
            🔗 Volledig Overzicht
          </div>
          <h2 className="text-2xl font-extrabold text-foreground mb-2">
            Roetfilter Center<br />Alle Diensten &amp; Locaties
          </h2>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Professionele roetfilter specialist<br />voor heel Oost-Vlaanderen, Antwerpen en Brussel.
          </p>
        </div>

        {/* Accordion sections */}
        <div className="space-y-3">

          {/* Services */}
          {showServices && (
            <Accordion
              title="Onze Diensten"
              badge="8 diensten"
              icon="🔧"
              badgeColor="bg-orange-100 text-orange-700"
              defaultOpen={variant === 'homepage' || variant === 'full'}
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {coreServices
                  .filter((s) => !currentService || s.href !== currentService)
                  .map((s) => (
                    <Link
                      key={s.href}
                      href={s.href}
                      className="group flex flex-col gap-1.5 p-3.5 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all"
                    >
                      <span className="text-xl">{s.icon}</span>
                      <span className="text-xs font-semibold text-foreground group-hover:text-primary leading-snug">{s.label}</span>
                      <span className="text-xs text-muted-foreground">{s.desc}</span>
                    </Link>
                  ))}
              </div>
              <div className="mt-4 pt-3 border-t border-border/50">
                <Link href="/diensten" className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                  Alle diensten bekijken →
                </Link>
              </div>
            </Accordion>
          )}

          {/* Brands */}
          {showBrands && (
            <Accordion
              title="Per Automerk"
              badge="16 merken"
              icon="🚗"
              badgeColor="bg-blue-100 text-blue-700"
            >
              <div className="flex flex-wrap gap-2">
                {brandPages
                  .filter((b) => !currentBrand || b.href !== currentBrand)
                  .map((b) => (
                    <Link
                      key={b.href}
                      href={b.href}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 hover:text-primary text-foreground transition-all"
                    >
                      <span className="w-4 h-4 rounded-full bg-secondary/70 text-foreground flex items-center justify-center text-[9px] font-extrabold">
                        {b.initial}
                      </span>
                      {b.label}
                    </Link>
                  ))}
              </div>
            </Accordion>
          )}

          {/* Priority cities */}
          {showCities && (
            <Accordion
              title="Prioriteit Steden"
              badge="12 steden"
              icon="📍"
              badgeColor="bg-green-100 text-green-700"
              defaultOpen={variant === 'city'}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-5">
                {priorityCities
                  .filter((c) => !currentSlug || c.href !== `/locaties/${currentSlug}`)
                  .map((c) => (
                    <Link
                      key={c.href}
                      href={c.href}
                      className="flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors py-1 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/40 group-hover:bg-primary transition-colors flex-shrink-0" />
                      Roetfilter Reinigen {c.label}
                    </Link>
                  ))}
              </div>
              <div className="pt-4 border-t border-border/50">
                <p className="text-xs font-extrabold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-secondary/70 flex items-center justify-center text-[10px]">📌</span>
                  Vaste Locatiepagina&apos;s
                </p>
                <div className="flex flex-wrap gap-2">
                  {dedicatedCityPages.map((p) => (
                    <Link
                      key={p.href}
                      href={p.href}
                      className="text-xs px-3 py-1.5 rounded-full bg-secondary/50 hover:bg-primary/10 hover:text-primary text-muted-foreground transition-all border border-transparent hover:border-primary/20"
                    >
                      {p.label}
                    </Link>
                  ))}
                </div>
              </div>
            </Accordion>
          )}

          {/* All regions */}
          {showAllRegions && (
            <Accordion
              title="Alle Regio&apos;s"
              badge="141 locaties"
              icon="🗺️"
              badgeColor="bg-blue-100 text-blue-700"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Oost-Vlaanderen */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <p className="text-xs font-extrabold text-blue-700 uppercase tracking-wider">
                      Oost-Vlaanderen
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {oostVlaanderenCities.slice(0, 12).map((c) => (
                      <Link
                        key={c.href}
                        href={c.href}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 hover:underline"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                  <Link
                    href="/locaties?province=Oost-Vlaanderen"
                    className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-blue-700 hover:underline"
                  >
                    Meer Oost-Vlaanderen →
                  </Link>
                </div>

                {/* Antwerpen */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <p className="text-xs font-extrabold text-red-700 uppercase tracking-wider">
                      Antwerpen
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {antwerpenCities.slice(0, 8).map((c) => (
                      <Link
                        key={c.href}
                        href={c.href}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 hover:underline"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                  <Link
                    href="/locaties?province=Antwerpen"
                    className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-red-700 hover:underline"
                  >
                    Meer Antwerpen →
                  </Link>
                </div>

                {/* Brussel */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-purple-500" />
                    <p className="text-xs font-extrabold text-purple-700 uppercase tracking-wider">
                      Brussel
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {brusselsCities.map((c) => (
                      <Link
                        key={c.href}
                        href={c.href}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 hover:underline"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-border text-center">
                <Link
                  href="/locaties"
                  className="inline-flex items-center gap-2 text-sm font-bold text-white bg-primary px-6 py-2.5 rounded-full hover:opacity-90 transition-opacity shadow-md"
                >
                  🗺️ Alle 141 locaties bekijken →
                </Link>
              </div>
            </Accordion>
          )}

          {/* City variant: compact region links */}
          {variant === 'city' && (
            <Accordion
              title="Andere Regio&apos;s"
              icon="🌍"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                {[...antwerpenCities.slice(0, 6), ...brusselsCities.slice(0, 6)].map((c) => (
                  <Link
                    key={c.href}
                    href={c.href}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 hover:underline"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
              <Link href="/locaties" className="inline-flex items-center gap-1 mt-4 text-xs font-bold text-primary hover:underline">
                Alle locaties bekijken →
              </Link>
            </Accordion>
          )}

          {/* Service/brand variant: compact city list */}
          {(variant === 'service' || variant === 'brand') && (
            <Accordion
              title="Locaties"
              icon="📍"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                {[...oostVlaanderenCities.slice(0, 8), ...antwerpenCities.slice(0, 4)].map((c) => (
                  <Link
                    key={c.href}
                    href={c.href}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 hover:underline"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
              <Link href="/locaties" className="inline-flex items-center gap-1 mt-4 text-xs font-bold text-primary hover:underline">
                Alle locaties →
              </Link>
            </Accordion>
          )}
        </div>

        {/* Bottom nav strip */}
        <div className="mt-10 pt-6 border-t border-border">
          <div className="flex flex-wrap gap-x-5 gap-y-2.5 justify-center text-xs text-muted-foreground">
            <Link href="/" className="hover:text-primary transition-colors font-semibold">Home</Link>
            <Link href="/over-roetfilter-center" className="hover:text-primary transition-colors">Over Ons</Link>
            <Link href="/veelgestelde-vragen" className="hover:text-primary transition-colors">FAQ</Link>
            <Link href="/contact" className="hover:text-primary transition-colors">Contact</Link>
            <Link href="/offerte" className="hover:text-primary transition-colors font-bold text-primary">Gratis Offerte</Link>
            <Link href="/locatie" className="hover:text-primary transition-colors">Onze Locatie</Link>
            <Link href="/locaties" className="hover:text-primary transition-colors">Alle Locaties</Link>
            <Link href="/diensten" className="hover:text-primary transition-colors">Alle Diensten</Link>
          </div>
        </div>

      </div>
    </section>
  );
}
